/**
 * Group Member Management API Functions
 * Handles SubMaster assignment and removal from groups
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../../auth/authUtils';
import { getApiGateway } from '../../api/endpointUtils';
import type {
  AssignSubMasterToGroupParams,
  AssignSubMasterToGroupResponse,
  UnassignSubMasterFromGroupParams,
  UnassignSubMasterFromGroupResponse,
  GetGroupUsersParams,
  GetGroupUsersResponse,
  AssignUserToDeviceParams,
  AssignUserToDeviceResponse,
  UnassignUserFromDeviceParams,
  UnassignUserFromDeviceResponse
} from '../types';

import { 
  ASSIGN_SUBMASTER_ERROR_MESSAGES,
  UNASSIGN_SUBMASTER_ERROR_MESSAGES,
  GET_GROUP_USERS_ERROR_MESSAGES,
  ASSIGN_USER_TO_DEVICE_ERROR_MESSAGES,
  UNASSIGN_USER_FROM_DEVICE_ERROR_MESSAGES
} from '../types';

/**
 * Assign SubMaster to a specific group (Master privilege only)
 * Assigns an existing SubMaster to manage a specific group
 * 
 * @param params - SubMaster assignment parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with SubMaster assignment response
 */
export async function assignSubMasterToGroup(params: Omit<AssignSubMasterToGroupParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<AssignSubMasterToGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: AssignSubMasterToGroupParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.guestEmail || requestParams.guestEmail.trim().length === 0) {
      throw new Error('Guest email is required');
    }
    
    if (!requestParams.groupManagementID || requestParams.groupManagementID.trim().length === 0) {
      throw new Error('Group management ID is required');
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestParams.guestEmail)) {
      throw new Error('Invalid guest email format');
    }

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/AssignSubMasterToGroup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: AssignSubMasterToGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = ASSIGN_SUBMASTER_ERROR_MESSAGES[data.resultcode as keyof typeof ASSIGN_SUBMASTER_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Unassign SubMaster from a specific group (Master privilege only)
 * Removes an existing SubMaster from managing a specific group
 * 
 * @param params - SubMaster unassignment parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with SubMaster unassignment response
 */
export async function unassignSubMasterFromGroup(params: Omit<UnassignSubMasterFromGroupParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<UnassignSubMasterFromGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: UnassignSubMasterFromGroupParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.guestEmail || requestParams.guestEmail.trim().length === 0) {
      throw new Error('Guest email is required');
    }
    
    if (!requestParams.groupManagementID || requestParams.groupManagementID.trim().length === 0) {
      throw new Error('Group management ID is required');
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestParams.guestEmail)) {
      throw new Error('Invalid guest email format');
    }

    // Make API request using DELETE method
    const response = await fetch(`${API_GATEWAY}/Account/DecoupleSubMasterFromGroup`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: UnassignSubMasterFromGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = UNASSIGN_SUBMASTER_ERROR_MESSAGES[data.resultcode as keyof typeof UNASSIGN_SUBMASTER_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Get list of users available for dashcam allocation
 * Retrieves invited users that are not yet assigned to any dashcam in groups
 * Used for assigning users to specific dashcams within groups
 * 
 * @param params - Group users query parameters (Email and UserToken from AuthStore if not provided)
 * @returns Promise with group users response
 */
export async function getGroupUsers(params: Omit<GetGroupUsersParams, 'Email' | 'UserToken'> & {
  Email?: string;
  UserToken?: string;
} = {}): Promise<GetGroupUsersResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetGroupUsersParams = {
      Email: authConfig.userEmail,
      UserToken: authConfig.userToken,
      TokenType: 'web',
      ...params
    };

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('Email', requestParams.Email);
    queryParams.append('UserToken', requestParams.UserToken);
    queryParams.append('TokenType', requestParams.TokenType || 'web');

    console.log('👥 [Group API] Requesting group users for allocation:', {
      Email: requestParams.Email,
      hasUserToken: !!requestParams.UserToken,
      TokenType: requestParams.TokenType
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/GetAllocationUserList?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetGroupUsersResponse = await response.json();

    console.log('👥 [Group API] Group users response received:', {
      resultcode: data.resultcode,
      hasUsers: !!data.response?.Users,
      userCount: data.response?.Users?.length || 0
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_GROUP_USERS_ERROR_MESSAGES[data.resultcode as keyof typeof GET_GROUP_USERS_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Group API] Group users request failed:', error);
    throw error;
  }
}

/**
 * Assign user to specific device within a group
 * Assigns invited users to specific devices for access control
 * Used for granular user-device assignment within groups
 * 
 * @param params - User allocation parameters (Email and UserToken from AuthStore if not provided)
 * @returns Promise with user assignment response
 */
export async function assignUserToDevice(params: Omit<AssignUserToDeviceParams, 'Email' | 'UserToken'> & {
  Email?: string;
  UserToken?: string;
}): Promise<AssignUserToDeviceResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: AssignUserToDeviceParams = {
      Email: authConfig.userEmail,
      UserToken: authConfig.userToken,
      TokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.GroupID || requestParams.GroupID.trim().length === 0) {
      throw new Error('Group ID is required');
    }
    
    if (!requestParams.PSN || requestParams.PSN.trim().length === 0) {
      throw new Error('PSN is required');
    }
    
    if (!requestParams.UserEmail || requestParams.UserEmail.length === 0) {
      throw new Error('User email list is required and cannot be empty');
    }
    
    // Validate email format in UserEmail array
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    requestParams.UserEmail.forEach((email, index) => {
      if (!email || email.trim().length === 0) {
        throw new Error(`User email at index ${index} cannot be empty`);
      }
      if (!emailRegex.test(email)) {
        throw new Error(`Invalid email format at index ${index}: ${email}`);
      }
    });

    console.log('👤 [Group API] Assigning users to device:', {
      Email: requestParams.Email,
      GroupID: requestParams.GroupID,
      PSN: requestParams.PSN,
      userCount: requestParams.UserEmail.length,
      hasUserToken: !!requestParams.UserToken,
      TokenType: requestParams.TokenType
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/AllocateUser`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: AssignUserToDeviceResponse = await response.json();

    console.log('👤 [Group API] User assignment response received:', {
      resultcode: data.resultcode,
      GroupID: requestParams.GroupID,
      PSN: requestParams.PSN,
      userCount: requestParams.UserEmail.length
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = ASSIGN_USER_TO_DEVICE_ERROR_MESSAGES[data.resultcode as keyof typeof ASSIGN_USER_TO_DEVICE_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Group API] User assignment request failed:', error);
    throw error;
  }
}

/**
 * Unassign user from specific device within a group
 * Removes assigned user from specific device access control
 * Used for removing user-device assignment within groups
 * 
 * @param params - User deallocation parameters (Email and UserToken from AuthStore if not provided)
 * @returns Promise with user unassignment response
 */
export async function unassignUserFromDevice(params: Omit<UnassignUserFromDeviceParams, 'Email' | 'UserToken'> & {
  Email?: string;
  UserToken?: string;
}): Promise<UnassignUserFromDeviceResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: UnassignUserFromDeviceParams = {
      Email: authConfig.userEmail,
      UserToken: authConfig.userToken,
      TokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.GroupID || requestParams.GroupID.trim().length === 0) {
      throw new Error('Group ID is required');
    }
    
    if (!requestParams.PSN || requestParams.PSN.trim().length === 0) {
      throw new Error('PSN is required');
    }
    
    if (!requestParams.UserEmail || requestParams.UserEmail.trim().length === 0) {
      throw new Error('User email is required');
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestParams.UserEmail)) {
      throw new Error(`Invalid email format: ${requestParams.UserEmail}`);
    }

    console.log('👤 [Group API] Unassigning user from device:', {
      Email: requestParams.Email,
      GroupID: requestParams.GroupID,
      PSN: requestParams.PSN,
      UserEmail: requestParams.UserEmail,
      hasUserToken: !!requestParams.UserToken,
      TokenType: requestParams.TokenType
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/DeallocateUser`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: UnassignUserFromDeviceResponse = await response.json();

    console.log('👤 [Group API] User unassignment response received:', {
      resultcode: data.resultcode,
      GroupID: requestParams.GroupID,
      PSN: requestParams.PSN,
      UserEmail: requestParams.UserEmail
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = UNASSIGN_USER_FROM_DEVICE_ERROR_MESSAGES[data.resultcode as keyof typeof UNASSIGN_USER_FROM_DEVICE_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Group API] User unassignment request failed:', error);
    throw error;
  }
}